import { useCallback, useEffect, useState } from 'react';

import type { DatabaseDefinition } from '@/api/configuration';
import {
    createDatabaseDefinition,
    deleteDatabaseDefinition,
    listDatabaseDefinitions,
    updateDatabaseDefinition,
} from '@/api/configuration';
import { DatabaseEnvironmentConfigSection } from '@/components/databases/database-environment-config-section';
import { useToastStore } from '@/stores/toast-store';

// --- Constants ---

const DATABASE_TYPES = ['mysql', 'postgresql', 'oracle', 'mssql'] as const;

const DATABASE_TYPE_LABELS: Record<(typeof DATABASE_TYPES)[number], string> = {
  mysql: 'MySQL',
  postgresql: 'PostgreSQL',
  oracle: 'Oracle',
  mssql: 'MS SQL Server',
};

// --- Database Definition Form ---

interface DatabaseDefinitionFormProps {
  initial?: DatabaseDefinition;
  onSave: (data: Omit<DatabaseDefinition, 'id' | 'projectKey'>) => void;
  onCancel: () => void;
}

function DatabaseDefinitionForm({ initial, onSave, onCancel }: DatabaseDefinitionFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<string>(initial?.type ?? '');
  const [port, setPort] = useState<string>(initial?.port != null ? String(initial.port) : '');
  const [databaseName, setDatabaseName] = useState(initial?.databaseName ?? '');
  const [username, setUsername] = useState(initial?.username ?? '');
  const [password, setPassword] = useState(initial?.password ?? '');
  const [patchScriptPath, setPatchScriptPath] = useState(initial?.patchScriptPath ?? '');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; type?: string; port?: string }>({});

  const validate = (): boolean => {
    const newErrors: { name?: string; type?: string; port?: string } = {};
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (type && !DATABASE_TYPES.includes(type as (typeof DATABASE_TYPES)[number])) {
      newErrors.type = 'Invalid database type';
    }
    if (port && (isNaN(Number(port)) || Number(port) < 1 || Number(port) > 65535)) {
      newErrors.port = 'Port must be a number between 1 and 65535';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      name: name.trim(),
      type: type ? (type as DatabaseDefinition['type']) : undefined,
      port: port ? Number(port) : undefined,
      databaseName: databaseName.trim() || undefined,
      username: username.trim() || undefined,
      password: password || undefined,
      patchScriptPath: patchScriptPath.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-surface-raised p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Name */}
        <div>
          <label htmlFor="db-name" className="block text-xs font-medium text-content-secondary mb-1">
            Name *
          </label>
          <input
            id="db-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. main-database"
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
          {errors.name && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.name}</p>}
        </div>

        {/* Type */}
        <div>
          <label htmlFor="db-type" className="block text-xs font-medium text-content-secondary mb-1">
            Database Type
          </label>
          <select
            id="db-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          >
            <option value="">— None —</option>
            {DATABASE_TYPES.map((t) => (
              <option key={t} value={t}>{DATABASE_TYPE_LABELS[t]}</option>
            ))}
          </select>
          {errors.type && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.type}</p>}
        </div>

        {/* Port */}
        <div>
          <label htmlFor="db-port" className="block text-xs font-medium text-content-secondary mb-1">
            Port
          </label>
          <input
            id="db-port"
            type="number"
            value={port}
            onChange={(e) => setPort(e.target.value)}
            placeholder="e.g. 3306"
            min={1}
            max={65535}
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
          {errors.port && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.port}</p>}
        </div>

        {/* Database Name */}
        <div>
          <label htmlFor="db-database-name" className="block text-xs font-medium text-content-secondary mb-1">
            Database Name
          </label>
          <input
            id="db-database-name"
            type="text"
            value={databaseName}
            onChange={(e) => setDatabaseName(e.target.value)}
            placeholder="e.g. corvus_db"
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>

        {/* Username */}
        <div>
          <label htmlFor="db-username" className="block text-xs font-medium text-content-secondary mb-1">
            Username
          </label>
          <input
            id="db-username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="e.g. db_admin"
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="db-password" className="block text-xs font-medium text-content-secondary mb-1">
            Password
          </label>
          <div className="relative">
            <input
              id="db-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full rounded-md border border-border bg-surface px-3 py-1.5 pr-10 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-content-tertiary hover:text-content-secondary focus:outline-none focus:ring-2 focus:ring-brand-600"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Patch Script Path */}
        <div className="sm:col-span-2">
          <label htmlFor="db-patch-path" className="block text-xs font-medium text-content-secondary mb-1">
            Patch Script Path <span className="text-content-tertiary">(directory in config git repo)</span>
          </label>
          <input
            id="db-patch-path"
            type="text"
            value={patchScriptPath}
            onChange={(e) => setPatchScriptPath(e.target.value)}
            placeholder="e.g. database/patches/main-db"
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
        >
          {initial ? 'Update' : 'Add Database'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// --- Main Component ---

interface DatabaseConfigTabProps {
  projectKey: string;
}

export function DatabaseConfigTab({ projectKey }: DatabaseConfigTabProps) {
  const [databases, setDatabases] = useState<DatabaseDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<DatabaseDefinition | null>(null);
  const [selectedDatabase, setSelectedDatabase] = useState<DatabaseDefinition | null>(null);
  const addToast = useToastStore((s) => s.addToast);

  const loadDatabases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listDatabaseDefinitions(projectKey);
      setDatabases(data);
    } catch {
      setError('Failed to load database definitions.');
    } finally {
      setLoading(false);
    }
  }, [projectKey]);

  useEffect(() => {
    if (projectKey) {
      loadDatabases();
    }
  }, [projectKey, loadDatabases]);

  const handleAdd = async (data: Omit<DatabaseDefinition, 'id' | 'projectKey'>) => {
    try {
      await createDatabaseDefinition(projectKey, data);
      setShowForm(false);
      addToast({ type: 'success', message: 'Database definition created successfully', duration: 5000 });
      loadDatabases();
    } catch {
      addToast({ type: 'error', message: 'Failed to create database definition', duration: 5000 });
    }
  };

  const handleEdit = async (data: Omit<DatabaseDefinition, 'id' | 'projectKey'>) => {
    if (!editing?.id) return;
    try {
      await updateDatabaseDefinition(projectKey, editing.id, data);
      setEditing(null);
      addToast({ type: 'success', message: 'Database definition updated successfully', duration: 5000 });
      loadDatabases();
    } catch {
      addToast({ type: 'error', message: 'Failed to update database definition', duration: 5000 });
    }
  };

  const handleDelete = async (db: DatabaseDefinition) => {
    if (!confirm(`Delete database definition "${db.name}"? This will also delete all environment-specific configurations for this database.`)) return;
    try {
      await deleteDatabaseDefinition(projectKey, db.id);
      addToast({ type: 'success', message: 'Database definition deleted successfully', duration: 5000 });
      loadDatabases();
    } catch {
      addToast({ type: 'error', message: 'Failed to delete database definition', duration: 5000 });
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-content">Database Definitions</h3>
        {!showForm && !editing && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-800 focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-offset-2 transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Database
          </button>
        )}
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 mb-3">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={() => { setError(null); loadDatabases(); }}
            className="mt-1 text-xs font-medium text-red-700 dark:text-red-300 underline"
          >
            Retry
          </button>
        </div>
      )}

      {loading && (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
          ))}
        </div>
      )}

      {showForm && (
        <div className="mb-3">
          <DatabaseDefinitionForm onSave={handleAdd} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {!loading && databases.length === 0 && !showForm && (
        <div className="rounded-lg border border-dashed border-border p-4 text-center">
          <p className="text-sm text-content-tertiary">No database definitions configured. Add one to manage database connections and run schema scripts.</p>
        </div>
      )}

      {!loading && databases.length > 0 && (
        <div className="rounded-lg border border-border divide-y divide-border">
          {databases.map((db) =>
            editing?.id === db.id ? (
              <div key={db.id} className="p-3">
                <DatabaseDefinitionForm
                  initial={db}
                  onSave={handleEdit}
                  onCancel={() => setEditing(null)}
                />
              </div>
            ) : (
              <div key={db.id} className="p-3 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-content">{db.name}</span>
                    {db.type && (
                      <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
                        {DATABASE_TYPE_LABELS[db.type]}
                      </span>
                    )}
                    {db.port && (
                      <span className="text-xs text-content-tertiary">
                        :{db.port}
                      </span>
                    )}
                    {db.databaseName && (
                      <span className="text-xs text-content-secondary font-mono truncate max-w-[200px]">
                        {db.databaseName}
                      </span>
                    )}
                  </div>
                  {db.patchScriptPath && (
                    <p className="mt-0.5 text-xs text-content-tertiary font-mono truncate">
                      {db.patchScriptPath}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setSelectedDatabase(selectedDatabase?.id === db.id ? null : db)}
                    className={`rounded px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1 ${
                      selectedDatabase?.id === db.id
                        ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                        : 'text-content-secondary hover:bg-surface-raised'
                    }`}
                  >
                    Environments
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(db)}
                    className="rounded px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(db)}
                    className="rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {selectedDatabase && (
        <DatabaseEnvironmentConfigSection
          projectKey={projectKey}
          databaseId={selectedDatabase.id}
          databaseName={selectedDatabase.name}
        />
      )}
    </section>
  );
}
